"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/auth/use-auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { EmailChannelConfigInput, NotificationChannelView } from "@/hooks/notifications/use-notification-channels";
import { useTranslations } from "next-intl"


interface EmailChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present = edit; absent = create. */
  channel?: NotificationChannelView | null;
  onSave: (name: string, config: EmailChannelConfigInput) => Promise<void>;
}

interface SenderCandidate {
  email: string;
  label: string;
  isPlatformDefault: boolean;
  isDefault: boolean;
}

/** Sentinel Select value for "auto-resolve" (Radix Select forbids empty values). */
const AUTO_SENDER = "__auto__";

/** Split a recipients textarea (commas or newlines) into trimmed addresses. */
function parseRecipients(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((r) => r.trim())
    .filter(Boolean);
}

export function EmailChannelDialog({ open, onOpenChange, channel, onSave }: EmailChannelDialogProps) {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsEmailChannelDialog")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth();
  const [name, setName] = useState("");
  const [recipients, setRecipients] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [subjectPrefix, setSubjectPrefix] = useState("");
  const [candidates, setCandidates] = useState<SenderCandidate[]>([]);
  const [saving, setSaving] = useState(false);

  // Re-seed the form whenever the dialog opens for a (different) channel.
  useEffect(() => {
    if (!open) return;
    const cfg = (channel?.config ?? {}) as Record<string, unknown>;
    setName(channel?.name ?? "");
    setRecipients(Array.isArray(cfg.recipients) ? (cfg.recipients as string[]).join("\n") : "");
    setFromEmail(typeof cfg.from_email === "string" ? cfg.from_email : "");
    setSubjectPrefix(typeof cfg.subject_prefix === "string" ? cfg.subject_prefix : "");
  }, [open, channel]);

  // The API rejects `from_email` on a system channel — hide the picker rather than
  // let the user choose something that 400s on save.
  const isSystem = channel?.isSystem ?? false;

  // Load the resolvable sender candidates (same set the send path can resolve),
  // so the operator can only pin an address that will actually be honored.
  useEffect(() => {
    if (!open || isSystem) return;
    let cancelled = false;
    fetchWithAuth("/api/agents/handover/sender-candidates")
      .then((r) => (r.ok ? r.json() : { candidates: [] }))
      .then((d) => {
        if (!cancelled) setCandidates(d.candidates ?? []);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isSystem, fetchWithAuth]);

  // Preserve a saved-but-now-unresolvable sender as a flagged option, so editing
  // an unrelated field doesn't silently drop it.
  const senderOptions = useMemo(() => {
    const list = candidates.map((c) => ({ value: c.email, label: c.label }));
    if (fromEmail && !candidates.some((c) => c.email === fromEmail)) {
      list.unshift({ value: fromEmail, label: `${fromEmail} (unavailable)` });
    }
    return list;
  }, [candidates, fromEmail]);

  const isEdit = Boolean(channel);
  const recipientList = parseRecipients(recipients);
  const canSave = name.trim().length > 0 && recipientList.length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(name.trim(), {
        recipients: recipientList,
        ...(isSystem ? {} : { from_email: fromEmail.trim() || null }),
        subject_prefix: subjectPrefix.trim() || null,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save channel");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <span key={isSystem ? tUi("editSystemEmailChannel") : isEdit ? tUi("editEmailChannel") : tUi("newEmailChannel")}>{isSystem ? tUi("editSystemEmailChannel") : isEdit ? tUi("editEmailChannel") : tUi("newEmailChannel")}</span>
          </DialogTitle>
          <DialogDescription>
            <span key={isSystem
              ? tUi("providedByVersafyNoInboxOfYourOwn")
              : tUi("yourTeamReceivesAStandaloneAlertAtThese")}>{isSystem
              ? tUi("providedByVersafyNoInboxOfYourOwn")
              : tUi("yourTeamReceivesAStandaloneAlertAtThese")}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="nc-email-name"><span key={tUi("name")}>{tUi("name")}</span></Label>
            <Input
              id="nc-email-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={tUi("supportTeam")}
              maxLength={100}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nc-email-recipients"><span key={tUi("recipients")}>{tUi("recipients")}</span></Label>
            <Textarea
              id="nc-email-recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder={"ops@example.com\nlead@example.com"}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              <span><span key={tUi("onePerLineOrCommaSeparatedUpTo")}>{tUi("onePerLineOrCommaSeparatedUpTo")}</span></span> {recipientList.length} <span><span key={tUi("address")}>{tUi("address")}</span></span>
              <span key={recipientList.length === 1 ? "" : "es"}>{recipientList.length === 1 ? "" : "es"}</span><span>.</span>
            </p>
          </div>

          {isSystem ? (
            <div className="space-y-1.5">
              <Label><span key={tUi("sender")}>{tUi("sender")}</span></Label>
              <p className="text-sm"><span key={tUi("versafyPlatformAddress")}>{tUi("versafyPlatformAddress")}</span></p>
              <p className="text-xs text-muted-foreground">
                <span key={tUi("fixedForThisChannelThatIsWhatLets")}>{tUi("fixedForThisChannelThatIsWhatLets")}</span></p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="nc-email-from"><span key={tUi("sender")}>{tUi("sender")}</span></Label>
              <Select
                value={fromEmail || AUTO_SENDER}
                onValueChange={(v) => setFromEmail(v === AUTO_SENDER ? "" : v)}
              >
                <SelectTrigger id="nc-email-from">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO_SENDER}><span key={tUi("autoYourSendingInboxPlatformDefault")}>{tUi("autoYourSendingInboxPlatformDefault")}</span></SelectItem>
                  {senderOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                <span key={tUi("onlyYourConnectedSendingInboxesAndThePlatform")}>{tUi("onlyYourConnectedSendingInboxesAndThePlatform")}</span></p>
              {!fromEmail && <p className="text-xs text-muted-foreground"><span key={tUi("automaticSenderHint")}>{tUi("automaticSenderHint")}</span></p>}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="nc-email-prefix"><span key={tUi("subjectPrefixOptional")}>{tUi("subjectPrefixOptional")}</span></Label>
            <Input
              id="nc-email-prefix"
              value={subjectPrefix}
              onChange={(e) => setSubjectPrefix(e.target.value)}
              placeholder={tUi("actionNeeded")}
              maxLength={120}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button onClick={handleSave} disabled={!canSave}>
            <span key={saving ? tUi("saving") : isEdit ? tUi("save") : tUi("create")}>{saving ? tUi("saving") : isEdit ? tUi("save") : tUi("create")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
